import { addWidgetParamOnLinkClick } from './widget'

function instantSearch(rootElement, fetchBaseUrl) {
  const searchInput = rootElement.querySelector('#search')
  const instantSearchElement = rootElement.querySelector('[data-fn-instant-search]')

  const inWidgetMode = !!rootElement.querySelector('html.widget')

  const getTextNoResults = instantSearchElement.getAttribute('data-gettext-no-results')
  const getTextResults = instantSearchElement.getAttribute('data-gettext-results')
  const getTextResultsIn = instantSearchElement.getAttribute('data-gettext-results-in')
  const getTextViewAll = instantSearchElement.getAttribute('data-gettext-view-all')

  const GA_CODE = 'UA-6180691-11'
  const debounceDelay = 400
  let timeout = false
  let resizeTimeout = false
  let timeoutUsedForFetch = false
  let searchInputSelectionStart
  let searchInputSelectionEnd

  function addInstantSearchKeyDownListener(event) {
    const resultLinks = instantSearchElement.querySelectorAll('a')
    const focusableElements = [searchInput, ...resultLinks]
    let currentPosition = -1

    function restoreSelectionRange() {
      window.setTimeout(() => {
        searchInput.setSelectionRange(
          searchInputSelectionStart, searchInputSelectionEnd,
        )
      }, 1)
    }

    function moveFocus(direction) {
      if (searchInput === rootElement.activeElement) {
        searchInputSelectionStart = searchInput.selectionStart
        searchInputSelectionEnd = searchInput.selectionEnd
      }

      if (currentPosition > -1) {
        const goTo = currentPosition + direction

        if (focusableElements[goTo]) {
          event.preventDefault()
          focusableElements[goTo].focus()

          if (searchInput === rootElement.activeElement) {
            restoreSelectionRange()
          }
        }
      }
    }

    if (timeout === timeoutUsedForFetch) {
      if (focusableElements.length > 1) {
        currentPosition = focusableElements.indexOf(rootElement.activeElement)

        if (currentPosition > 0 && event.key === 'Escape') {
          searchInput.focus()
          restoreSelectionRange()
        }

        if (event.key === 'ArrowUp') {
          moveFocus(-1)
        } else if (event.key === 'ArrowDown') {
          moveFocus(+1)
        }
      }
    }
  }

  function clearInstantSearch() {
    instantSearchElement.innerHTML = ''
  }

  function inputAriaExpandedStatus(status) {
    const searchFormElement = rootElement.querySelector('.fn-search-form')
    const input = searchFormElement.querySelector('.fn-search-form__input')
    input.setAttribute('aria-expanded', status) // true or false
  }

  function removeInstantSearchBackdrop() {
    document.removeEventListener('keydown', addInstantSearchKeyDownListener)
    const backdrop = rootElement.querySelector('.fn-backdrop')

    if (backdrop) {
      backdrop.parentNode.removeChild(backdrop)
    }

    document.body.style.minHeight = null
    instantSearchElement.style.height = 0
    rootElement.querySelector('.fn-search-form__input-group').classList.remove('search-form__input-group--focused')

    inputAriaExpandedStatus('false')
  }

  function addSearchBackdrop() {
    if (!rootElement.querySelector('.fn-backdrop')) {
      if (inWidgetMode) {
        rootElement.querySelector('.fn-content-wrapper').insertAdjacentHTML('beforeend', '<div class="backdrop fn-backdrop"></div>')
      } else {
        rootElement.querySelector('.search-form').insertAdjacentHTML('afterend', '<div class="backdrop fn-backdrop"></div>')
      }

      document.removeEventListener('keydown', addInstantSearchKeyDownListener)
      document.addEventListener('keydown', addInstantSearchKeyDownListener)

      rootElement.querySelector('.fn-backdrop').addEventListener('touchmove', (event) => {
        event.preventDefault()
      })
      rootElement.querySelector('.fn-backdrop').addEventListener('click', () => {
        clearInstantSearch()
        removeInstantSearchBackdrop()
      })
      rootElement.querySelector('.fn-search-form__input-group').classList.add('search-form__input-group--focused')

      inputAriaExpandedStatus('true')
    }
  }

  function setInstantSearchSize() {
    const childElement = instantSearchElement.querySelector('div')
    const childHeight = childElement.getBoundingClientRect().height
    const paddingTop = +window.getComputedStyle(instantSearchElement, null).getPropertyValue('padding-top').replace(/\D/g, '')
    const minBodyHeight = childElement.getBoundingClientRect().top
      + childElement.getBoundingClientRect().height
    let instantSearchElementHeight

    if (inWidgetMode) {
      const resultsList = rootElement.querySelector('.instant-search__results-list')
      instantSearchElement.style.maxHeight = `calc((100vh - ${childElement.getBoundingClientRect().top}px) - 0.5rem)`
      instantSearchElement.style.top = `calc(${rootElement.querySelector('.header').getBoundingClientRect().height}px - 16px)`

      if (resultsList) {
        const resultsListBorderTopWidth = +window.getComputedStyle(resultsList, null).getPropertyValue('border-top-width').replace(/\D/g, '')

        const resultItemsArray = [].slice.call(rootElement.querySelectorAll('.instant-search__result-item'))
        const resultItemsHeights = resultItemsArray.map(item => item.getBoundingClientRect().height)
        let totalHeight = resultItemsHeights.reduce((accumulator, current) => accumulator + current)

        const linkToSearchPage = rootElement.querySelector('.instant-search__link-to-search-page')

        if (linkToSearchPage) {
          totalHeight += linkToSearchPage.getBoundingClientRect().height
        }

        instantSearchElementHeight = `${paddingTop + totalHeight + resultsListBorderTopWidth}px`
      } else {
        instantSearchElementHeight = `${paddingTop + childHeight}px`
      }
    } else {
      if (minBodyHeight > document.body.getBoundingClientRect().height) {
        document.body.style.minHeight = `calc(${minBodyHeight}px + 5rem)`
      }

      instantSearchElementHeight = `${paddingTop + childHeight}px`
    }

    instantSearchElement.style.height = instantSearchElementHeight
  }

  function onResize() {
    window.clearTimeout(resizeTimeout)
    resizeTimeout = window.setTimeout(setInstantSearchSize, debounceDelay)
  }

  function generateInstantSearchResultsMarkup(data, requestUrl) {
    data.forum.forEach((result) => {
      result.isForumResult = true
    })

    const allResults = data.knowledge.concat(data.forum)
    const limit = 5
    let forumResultsIncluded = false

    const results = allResults.slice(0, limit)
    const resultsCount = data.total_hits

    const a11yText = document.createTextNode(`${resultsCount} ${getTextResults}`)
    const newDivA11yText = document.createElement('div')
    newDivA11yText.setAttribute('aria-live', 'assertive')
    newDivA11yText.classList.add('visuallyhidden')
    newDivA11yText.appendChild(a11yText)

    const newUl = document.createElement('ul')
    newUl.classList.add('instant-search__results-list')
    newUl.setAttribute('role', 'listbox')
    newUl.setAttribute('id', 'search-listbox')

    results.forEach((result) => {
      if (result.isForumResult && !forumResultsIncluded) {
        const newTextForum = document.createTextNode(`${getTextResultsIn} ${data.forum_name}`)
        const newLiForum = document.createElement('li')
        newLiForum.classList.add('instant-search__result-forum')
        newLiForum.appendChild(newTextForum)
        newUl.appendChild(newLiForum)
        forumResultsIncluded = true
      }

      const newLi = document.createElement('li')
      newLi.classList.add('instant-search__result-item')
      newLi.setAttribute('role', 'option')
      newLi.insertAdjacentHTML('beforeend', `<a href="${result.url}" class="instant-search__result-title" aria-label="${result.title}">${result.title}</a>`)
      newLi.insertAdjacentHTML('beforeend', `<div class="instant-search__result-description">${result.body}</div>`)

      if (result.categories) {
        const resultCategories = document.createElement('span')
        resultCategories.classList.add('instant-search__result-categories')

        result.categories.forEach((name) => {
          const elm = document.createElement('span')
          elm.textContent = name
          resultCategories.appendChild(elm)
        })

        newLi.appendChild(resultCategories)
      }

      newUl.appendChild(newLi)
    })

    const newDivWrapper = document.createElement('div')
    newDivWrapper.classList.add('instant-search__wrapper')
    newDivWrapper.appendChild(newDivA11yText)
    newDivWrapper.appendChild(newUl)

    if (resultsCount > limit) {
      const newContentAll = document.createTextNode(`${getTextViewAll} ${resultsCount} ${getTextResults}`)
      const newLinkAll = document.createElement('a')
      newLinkAll.classList.add('more-link')
      newLinkAll.setAttribute('href', requestUrl)
      const body = instantSearchElement.closest('body')
      const angleRight = body.querySelector('svg[class="icon more-link__icon"], svg[class="icon icon--stroke link-list__icon"]').cloneNode(true)
      angleRight.classList.remove("icon--stroke")
      angleRight.classList.remove("link-list__icon")
      angleRight.classList.add("more-link__icon")
      newLinkAll.insertAdjacentElement('beforeend', angleRight)
      newLinkAll.appendChild(newContentAll)
      const newDiv = document.createElement('div')
      newDiv.appendChild(newLinkAll)
      newDiv.classList.add('instant-search__link-to-search-page')
      newDivWrapper.appendChild(newDiv)
    }

    instantSearchElement.appendChild(newDivWrapper)

    if (inWidgetMode) {
      addWidgetParamOnLinkClick(instantSearchElement)
    }
  }

  function dispatchResultsUpdatedEvent() {
    const searchResultsUpdatedEvent = new CustomEvent('kundo-knowledge:search-results-updated', {
      detail: instantSearchElement,
    })
    document.dispatchEvent(searchResultsUpdatedEvent)
  }

  function getInstantSearchResults(searchString = searchInput.value) {
    if (searchString.length < 3) {
      clearInstantSearch()
      removeInstantSearchBackdrop()
      window.removeEventListener('resize', onResize)
    } else {
      const thisTimeoutId = timeout
      const requestUrl = `/search?format=json&q=${encodeURIComponent(searchString).replace(/%20/g, '+')}`

      fetch(`${fetchBaseUrl}${requestUrl}`, {
        credentials: 'same-origin',
        cache: 'no-cache',
        headers: {
          Accept: 'application/json',
        },
      })
        .then(resp => resp.json())
        .then((json) => {
          // check that this is the most recent fetch
          if (thisTimeoutId !== timeout) {
            return
          }

          timeoutUsedForFetch = timeout
          clearInstantSearch()

          if ((json.knowledge && json.knowledge.length) || (json.forum && json.forum.length)) {
            generateInstantSearchResultsMarkup(json, requestUrl)
            dispatchResultsUpdatedEvent()
          } else {
            const newContent = document.createTextNode(getTextNoResults)
            const newDiv = document.createElement('div')
            newDiv.classList.add('instant-search__no-results')
            newDiv.setAttribute('aria-live', 'assertive')
            newDiv.appendChild(newContent)
            instantSearchElement.appendChild(newDiv)
          }

          addSearchBackdrop()
          setInstantSearchSize()
          window.addEventListener('resize', onResize)
        })
        .then(() => window.gtag && window.gtag('config', GA_CODE, {
          page_location: window.location.origin + requestUrl,
        }))
        .catch(() => {
          clearInstantSearch()
          removeInstantSearchBackdrop()
          window.removeEventListener('resize', onResize)
        })
    }
  }

  searchInput.setAttribute('autocomplete', 'off')
  searchInput.setAttribute('autocorrect', 'off')
  searchInput.setAttribute('autocapitalize', 'off')
  searchInput.setAttribute('spellcheck', 'false')

  instantSearchElement.addEventListener('touchmove', (event) => {
    if (!event.target.closest('.instant-search__result-item')) {
      event.preventDefault()
    }
  })

  searchInput.addEventListener('input', () => {
    window.clearTimeout(timeout)
    timeout = window.setTimeout(getInstantSearchResults, debounceDelay)
  })

  searchInput.addEventListener('focus', () => {
    if (!rootElement.querySelector('.fn-backdrop') && searchInput.value.length > 2) {
      getInstantSearchResults()
    }
  })
}

export default instantSearch
